import React, { useEffect, useState } from 'react';
import { Download, Filter } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { formatINR, formatDate } from '@/lib/utils';

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ start: '', end: '', purpose: '' });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const { data } = await api.get('/admin/reports/payments', { params });
      setRows(data);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

  const exportCSV = () => {
    const headers = ['Payment ID', 'Customer', 'Email', 'Purpose', 'Reference', 'Amount', 'Mode', 'Date'];
    const lines = rows.map((r) => [
      r.razorpay_payment_id, r.customer_name, r.customer_email, r.purpose, r.reference_id.slice(0, 8),
      r.amount, r.mode, formatDate(r.created_at),
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `kba-payments-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-payments">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Audit</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Payments Report</h1>
          <div className="mt-3 text-sm text-white/60">Total filtered: <span className="font-mono text-volt">{formatINR(total)}</span></div>
        </div>
        <Button variant="outline" onClick={exportCSV} data-testid="export-payments"><Download size={14} /> Export CSV</Button>
      </div>

      <div className="bg-surface border border-white/10 p-5 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Field label="From"><Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} /></Field>
        <Field label="To"><Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} /></Field>
        <Field label="Purpose">
          <select value={filters.purpose} onChange={(e) => setFilters({ ...filters, purpose: e.target.value })} className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none">
            <option value="">All</option>
            <option value="booking">Booking</option>
            <option value="membership">Membership</option>
          </select>
        </Field>
        <div className="flex items-end"><Button onClick={load} className="w-full"><Filter size={14} /> Apply</Button></div>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="border-b border-white/10 bg-obsidian/40">
            <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              <th className="text-left px-5 py-3">Payment ID</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Purpose</th>
              <th className="text-right px-5 py-3">Amount</th>
              <th className="text-left px-5 py-3">Mode</th>
              <th className="text-left px-5 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-5 py-12 text-center text-white/40">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="6" className="px-5 py-12 text-center text-white/40">No payments recorded yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-5 py-3 font-mono text-xs text-white/80">{r.razorpay_payment_id}</td>
                <td className="px-5 py-3">
                  <div className="font-medium">{r.customer_name}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">{r.customer_email}</div>
                </td>
                <td className="px-5 py-3"><Badge>{r.purpose}</Badge></td>
                <td className="px-5 py-3 text-right font-mono text-volt">{formatINR(r.amount)}</td>
                <td className="px-5 py-3"><Badge tone={r.mode === 'live' ? 'success' : 'default'}>{r.mode}</Badge></td>
                <td className="px-5 py-3 text-white/60">{formatDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
